import React, { useEffect, useState } from 'react'
import { Button } from 'semantic-ui-react'
import { deleteAccount } from 'utility/services/crm'
import { bandTone, healthHeadline, safeLink } from 'utility/crm/health'
import { ALL_CRM_SCOPES, invalidateCrm, useAccountDetail, useCrmToast, useIsCrmAdmin } from 'hooks/crm'
import { ConfirmDialog } from './CrmOverlay'
import { AccountContacts, AccountDeals, AccountTimeline, AuditsTicketsTab, HealthTab } from './AccountTabs'
import { AsyncState, Avatar, Muted, Pill, Row, TabBar, TabButton } from './ui'

const TABS = [
    ['health', 'Health'],
    ['audits', 'Audits & tickets'],
    ['timeline', 'Timeline'],
    ['deals', 'Deals'],
    ['contacts', 'Contacts'],
]

/**
 * One account: header with the health headline, then Health / Audits & tickets / Timeline / Deals / Contacts.
 * @param {{
 *   accountId: number,
 *   onEdit: (account: import('utility/crm/types').CrmAccount) => void,
 *   onDeleted: () => void,
 *   onOpenContact: (id: number) => void,
 *   onOpenDeal: (id: number) => void,
 *   onNewDeal: (account: import('utility/crm/types').CrmAccount) => void,
 * }} props
 */
export const AccountDetail = ({ accountId, onEdit, onDeleted, onOpenContact, onOpenDeal, onNewDeal }) => {
    const isAdmin = useIsCrmAdmin()
    const { successToast, showError } = useCrmToast()
    const query = useAccountDetail(accountId)
    const [tab, setTab] = useState('health')
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [busy, setBusy] = useState(false)
    // Ignore a stale record while another account is loading.
    const detail = query.data && query.data.Account.AccountId === accountId ? query.data : null
    const account = detail?.Account

    useEffect(() => {
        setTab('health')
    }, [accountId])

    const remove = async () => {
        setBusy(true)
        try {
            await deleteAccount(accountId)
            successToast('Account deleted')
            invalidateCrm(...ALL_CRM_SCOPES)
            setConfirmDelete(false)
            onDeleted()
        } catch (err) {
            showError(err)
            setConfirmDelete(false)
        } finally {
            setBusy(false)
        }
    }

    const site = safeLink(account?.Website)

    return (
        <AsyncState loading={query.loading} error={query.error} hasData={Boolean(detail)} onRetry={query.refetch}>
            {account ? (
                <div>
                    <Row $between $wrap style={{ alignItems: 'flex-start', marginBottom: 12 }}>
                        <Row $gap={12} style={{ alignItems: 'flex-start' }}>
                            <Avatar name={account.Name} size={44} />
                            <div>
                                <h3 style={{ margin: 0 }}>{account.Name}</h3>
                                <Muted>
                                    {[account.Industry, account.City, account.Sites?.length ? `${account.Sites.length} site${account.Sites.length === 1 ? '' : 's'}` : null, account.OwnerName ? `owner ${account.OwnerName.trim()}` : null]
                                        .filter(Boolean)
                                        .join(' · ')}
                                </Muted>
                                {account.Website ? (
                                    <div>
                                        {site ? (
                                            <a href={site} target="_blank" rel="noopener noreferrer">
                                                {account.Website}
                                            </a>
                                        ) : (
                                            <span>{account.Website}</span>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        </Row>
                        <Row $gap={8} $wrap>
                            {account.Health ? <Pill $tone={bandTone(account.Health.Band)}>{healthHeadline(account.AccountType, account.Health)}</Pill> : <Pill>{account.AccountType}</Pill>}
                            <Button type="button" size="small" onClick={() => onEdit(account)}>
                                Edit
                            </Button>
                            {isAdmin ? (
                                <Button type="button" size="small" basic negative onClick={() => setConfirmDelete(true)}>
                                    Delete
                                </Button>
                            ) : null}
                        </Row>
                    </Row>

                    <TabBar role="tablist" aria-label="Account sections">
                        {TABS.map(([key, text]) => (
                            <TabButton key={key} type="button" role="tab" aria-selected={tab === key} $active={tab === key} onClick={() => setTab(key)}>
                                {text}
                            </TabButton>
                        ))}
                    </TabBar>

                    <div role="tabpanel">
                        {tab === 'health' ? <HealthTab health={account.Health} /> : null}
                        {tab === 'audits' ? <AuditsTicketsTab inspections={detail.Inspections} tickets={detail.Tickets} isCustomer={Boolean(account.CustomerId)} /> : null}
                        {tab === 'timeline' ? <AccountTimeline accountId={accountId} /> : null}
                        {tab === 'deals' ? <AccountDeals deals={detail.Deals} onOpenDeal={onOpenDeal} onNewDeal={() => onNewDeal(account)} /> : null}
                        {tab === 'contacts' ? <AccountContacts contacts={detail.Contacts} onOpenContact={onOpenContact} /> : null}
                    </div>

                    <ConfirmDialog
                        open={confirmDelete}
                        danger
                        title="Delete account"
                        message={`Delete ${account.Name}? Its contacts, deals and follow-ups are removed too. This cannot be undone.`}
                        confirmLabel="Delete"
                        loading={busy}
                        onConfirm={remove}
                        onClose={() => setConfirmDelete(false)}
                    />
                </div>
            ) : null}
        </AsyncState>
    )
}
